import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchDashboard, updateTask, confirmCalendarSuggestion,
  fetchBriefingPreview, fetchTaskInsights, fetchHealthScore, fetchHealthHistory,
  fetchGoogleCalendarStatus,
} from "@/lib/api";
import { DASHBOARD, BRIEFING, HEALTH, INSIGHTS } from "@/constants/testIds";
import { formatDay, formatTime, classFor, childChip, categoryIcon } from "@/lib/format";
import * as LucideIcons from "lucide-react";
import {
  Sparkles, MessageSquare, Bell, ListChecks, Calendar, Inbox, ArrowRight, MapPin, Check, BookOpen,
  Activity, TrendingUp, TrendingDown, Minus, Lightbulb, BookHeart,
} from "lucide-react";
import { toast } from "sonner";
import FirstRunChecklist from "@/components/FirstRunChecklist";
import FamilyCalendar from "@/components/FamilyCalendar";
import WeeklySnapshot from "@/components/WeeklySnapshot";
import FamilyIntelligence from "@/components/FamilyIntelligence";
import ParentCommandCenter from "@/components/ParentCommandCenter";
import RecommendationsCard from "@/components/RecommendationsCard";
import useTrackPage from "@/hooks/useTrackPage";

export default function Dashboard() {
  useTrackPage("dashboard_open");
  const [data, setData] = useState(null);
  const [briefingPreview, setBriefingPreview] = useState(null);
  const [insights, setInsights] = useState({});
  const [health, setHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [gcalStatus, setGcalStatus] = useState(null);

  const load = () => fetchDashboard().then(setData);
  useEffect(() => {
    load();
    fetchBriefingPreview().then(setBriefingPreview).catch(() => {});
    fetchTaskInsights().then(r => setInsights(r.insights || {})).catch(() => {});
    fetchHealthScore().then(setHealth).catch(() => {});
    fetchHealthHistory().then(setHealthHistory).catch(() => {});
    fetchGoogleCalendarStatus().then(setGcalStatus).catch(() => {});
  }, []);

  const onSnapshotLoaded = useCallback((s) => setSnapshot(s || null), []);

  if (!data) return <div className="text-[var(--fcc-muted)]">Loading your family…</div>;

  return (
    <div data-testid={DASHBOARD.page} className="space-y-10">
      <FirstRunChecklist />

      {/* Google Calendar badge — visible at top once Gmail is connected */}
      {gcalStatus && gcalStatus.connected && (
        <GoogleCalendarBadge status={gcalStatus} />
      )}

      {/* PARENT COMMAND CENTER — answers above the calendar */}
      <ParentCommandCenter />

      {/* SMART RECOMMENDATIONS */}
      <RecommendationsCard />

      {/* HERO: Family Calendar — visual centerpiece */}
      <FamilyCalendar onSnapshotLoaded={onSnapshotLoaded} />

      {/* WEEKLY SNAPSHOT — compact stats above the fold under the calendar */}
      <WeeklySnapshot snapshot={snapshot} briefingPreview={briefingPreview} />

      {/* FAMILY HEALTH SCORE */}
      {health && <HealthScoreCard health={health} history={healthHistory} />}

      {/* FAMILY INTELLIGENCE — compact rows replacing the old big cards */}
      <FamilyIntelligence
        briefingPreview={briefingPreview}
        pendingTasks={data.pending_tasks}
        calendarSuggestions={data.calendar_suggestions}
      />

      {/* MEMORIES + COPILOT shortcuts */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/app/memories"
          data-testid="dashboard-memories-cta"
          className="fcc-card p-5 flex items-center gap-4 hover:bg-[var(--fcc-cream-2)] transition"
        >
          <span className="w-11 h-11 rounded-2xl bg-[var(--fcc-green-soft)] flex items-center justify-center shrink-0">
            <BookHeart className="w-4 h-4 text-[var(--fcc-green)]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="label-overline">Memories</p>
            <p className="font-display text-lg text-[var(--fcc-ink)] mt-0.5">Your family year in review</p>
            <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">Per-child activity mix, recent highlights, briefings.</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[var(--fcc-muted)]" />
        </Link>
        <Link
          to="/app/copilot"
          data-testid="dashboard-copilot-cta"
          className="fcc-card p-5 flex items-center gap-4 hover:bg-[var(--fcc-cream-2)] transition"
        >
          <span className="w-11 h-11 rounded-2xl bg-[var(--fcc-emma)] flex items-center justify-center shrink-0">
            <MessageSquare className="w-4 h-4 text-[var(--fcc-green)]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="label-overline">Ask Copilot</p>
            <p className="font-display text-lg text-[var(--fcc-ink)] mt-0.5">&ldquo;What&apos;s on Saturday?&rdquo;</p>
            <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">Conversational answers from your real family data.</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[var(--fcc-muted)]" />
        </Link>
      </section>

      {/* THIS WEEK'S PRIORITIES (briefing banner) */}
      <SectionHeader eyebrow="This week&apos;s priorities" title="From your weekly briefing" subtitle="AI-generated top of mind for the next 7 days." />
      <BriefingBanner preview={briefingPreview} />

      {/* ACTION CENTER */}
      <SectionHeader eyebrow="Action center" title="Forms, payments and confirmations" subtitle="Each item shows why it matters — a one-line Family Insight from Claude." />
      <div className="grid lg:grid-cols-2 gap-6">
        <ActionRequired tasks={data.pending_tasks} reload={load} insights={insights} />
        <CalendarSuggestionsCard items={data.calendar_suggestions} reload={load} />
      </div>

      <CategoryBreakdown byCategory={data.by_category} />
    </div>
  );
}

function GoogleCalendarBadge({ status }) {
  if (status.needs_reconnect) {
    return (
      <div data-testid="gcal-badge-reconnect" className="fcc-card-accent p-4 sm:p-5 flex flex-wrap items-center gap-3 justify-between border-l-4 border-l-amber-500">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-amber-700" />
          </span>
          <div>
            <p className="font-semibold text-[var(--fcc-ink)]">Reconnect Google to enable Calendar</p>
            <p className="text-xs text-[var(--fcc-ink-soft)] mt-0.5">
              KidHQ can read your calendar + create approved events once you grant Calendar permissions.
            </p>
          </div>
        </div>
        <Link to="/app/gmail" className="fcc-btn-primary text-sm py-2 px-4">Reconnect Google →</Link>
      </div>
    );
  }
  if (status.can_write) {
    return (
      <div data-testid="gcal-badge-connected" className="fcc-card p-4 flex flex-wrap items-center justify-between gap-3 border-l-4 border-l-[var(--fcc-green)]">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center">
            <Calendar className="w-4 h-4 text-[var(--fcc-green)]" />
          </span>
          <div>
            <p className="text-sm font-semibold text-[var(--fcc-ink)]">Google Calendar connected · write enabled</p>
            <p className="text-xs text-[var(--fcc-muted)] mt-0.5">
              Detected events can be approved to your calendar with one click.
              {status.auto_add_trusted ? " Trusted-sender auto-add is ON." : ""}
            </p>
          </div>
        </div>
        <Link to="/app/settings" className="text-sm font-semibold text-[var(--fcc-green)]">Manage →</Link>
      </div>
    );
  }
  return null;
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div>
      <p className="label-overline">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl text-[var(--fcc-ink)] mt-1 leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function AlertsSummary({ preview }) {
  return (
    <div className="fcc-card p-6 sm:p-7 grid sm:grid-cols-2 gap-4 items-stretch">
      <div className="border border-[var(--fcc-border)] rounded-2xl p-5 bg-[var(--fcc-red-soft)]">
        <p className="label-overline text-[var(--fcc-red,#a23527)]">Risk flags</p>
        <p className="font-display text-5xl text-[var(--fcc-ink)] mt-2">{preview.risk_count}</p>
        <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">Things that could go wrong if ignored.</p>
      </div>
      <div className="border border-[var(--fcc-border)] rounded-2xl p-5" style={{ background: "var(--fcc-peach)" }}>
        <p className="label-overline">Schedule conflicts</p>
        <p className="font-display text-5xl text-[var(--fcc-ink)] mt-2">{preview.conflict_count}</p>
        <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">Double-bookings and timing clashes.</p>
      </div>
      <div className="sm:col-span-2 text-right">
        <Link to="/app/briefing" className="fcc-btn-outline">
          Review in weekly briefing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function HeadlineCard({ headline, prepList, today }) {
  const dateStr = today.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  return (
    <div className="fcc-card overflow-hidden">
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="fcc-pill fcc-chip-emma">Family Command Center</span>
            <span className="fcc-pill" style={{ background: "var(--fcc-peach)", color: "var(--fcc-ink)" }}>{dateStr}</span>
          </div>
          <h1 className="font-display text-[40px] sm:text-[56px] leading-[1.05] text-[var(--fcc-ink)] mt-6">
            Here is what needs your attention.
          </h1>
          <p className="text-[var(--fcc-ink-soft)] mt-5 max-w-2xl leading-relaxed">{headline}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/app/copilot" data-testid={DASHBOARD.askCopilotBtn} className="fcc-btn-primary">
              <MessageSquare className="w-4 h-4" /> Ask Copilot
            </Link>
            <Link to="/app/detected" className="fcc-btn-outline">
              See detected emails <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <div className="flex items-center gap-2 text-[var(--fcc-green)]">
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold text-sm">Tonight&apos;s prep list</span>
          </div>
          <ul className="mt-5 space-y-3">
            {prepList.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">Nothing burning. Enjoy the evening.</li>}
            {prepList.map(p => (
              <li key={p.id} className="bg-white border border-[var(--fcc-border)] rounded-2xl p-4">
                <p className="font-semibold text-[var(--fcc-ink)] leading-snug">{p.title}</p>
                <p className="text-[11px] uppercase tracking-widest mt-1 text-[var(--fcc-muted)]">{p.child_name} · {p.priority}</p>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}

function KPI({ label, value, caption, Icon, tint, testId }) {
  return (
    <div data-testid={testId} className="fcc-card p-6">
      <div className="flex items-start justify-between">
        <span className="text-sm font-semibold text-[var(--fcc-ink-soft)]">{label}</span>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: tint }}>
          <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
        </span>
      </div>
      <div className="font-display text-5xl text-[var(--fcc-ink)] mt-4">{value}</div>
      <p className="text-xs text-[var(--fcc-muted)] mt-1">{caption}</p>
    </div>
  );
}

function ActionRequired({ tasks, reload, insights = {} }) {
  const complete = async (t) => {
    await updateTask(t.id, !t.completed);
    toast.success(!t.completed ? "Marked done" : "Marked open");
    reload();
  };
  return (
    <div className="fcc-card p-7">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-overline">Action required</p>
          <h3 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">Next parent actions</h3>
        </div>
        <Link to="/app/tasks" className="text-sm font-semibold text-[var(--fcc-green)]">View all →</Link>
      </div>
      <ul className="mt-6 space-y-3">
        {tasks.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">All caught up.</li>}
        {tasks.map(t => (
          <li key={t.id} data-testid={DASHBOARD.taskItem} className="border border-[var(--fcc-border)] rounded-2xl p-4 flex gap-4 items-start">
            <button onClick={() => complete(t)} className="w-6 h-6 rounded-full border-2 border-[var(--fcc-green)] flex items-center justify-center mt-0.5 shrink-0 hover:bg-[var(--fcc-green-soft)]" aria-label="toggle">
              {t.completed && <Check className="w-3.5 h-3.5 text-[var(--fcc-green)]" />}
            </button>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-[var(--fcc-ink)] leading-snug">{t.title}</p>
                <span className={`fcc-pill ${classFor(t.priority)}`}>{t.priority}</span>
              </div>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{t.description}</p>
              {insights[t.id] && (
                <div
                  data-testid={INSIGHTS.taskInsight}
                  className="mt-3 flex items-start gap-2 bg-[var(--fcc-green-soft)] border border-[var(--fcc-border)] rounded-xl p-3"
                >
                  <Lightbulb className="w-4 h-4 text-[var(--fcc-green)] shrink-0 mt-0.5" />
                  <p className="text-sm text-[var(--fcc-ink)] leading-snug italic">{insights[t.id]}</p>
                </div>
              )}
              <p className="text-[11px] uppercase tracking-widest mt-2 text-[var(--fcc-muted)]">{t.child_name} · {t.due_date ? new Date(t.due_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "No date"}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CalendarSuggestionsCard({ items, reload }) {
  const confirm = async (s) => {
    await confirmCalendarSuggestion(s.id);
    toast.success(`Added "${s.title}" to your calendar.`);
    reload();
  };
  return (
    <div className="fcc-card p-7">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-overline">Calendar suggestions</p>
          <h3 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">What belongs on the calendar</h3>
        </div>
        <Link to="/app/calendar" className="text-sm font-semibold text-[var(--fcc-green)]">Review →</Link>
      </div>
      <ul className="mt-6 space-y-3">
        {items.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">Nothing to confirm right now.</li>}
        {items.map(s => (
          <li key={s.id} className="border border-[var(--fcc-border)] rounded-2xl p-4 flex justify-between gap-4 items-start">
            <div className="flex-1">
              <p className="font-semibold text-[var(--fcc-ink)]">{s.title}</p>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{formatDay(s.event_date)} at {formatTime(s.event_date)} {s.child_name ? `· ${s.child_name}` : ""}</p>
              {s.location && <p className="text-xs text-[var(--fcc-muted)] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</p>}
            </div>
            <button data-testid={DASHBOARD.suggestionConfirm} onClick={() => confirm(s)} className="fcc-btn-primary text-sm py-2 px-4">
              <Calendar className="w-4 h-4" /> Confirm
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CategoryBreakdown({ byCategory }) {
  const entries = Object.entries(byCategory).filter(([, v]) => v > 0);
  return (
    <div className="fcc-card p-7">
      <p className="label-overline">By category</p>
      <h3 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">Where the noise comes from</h3>
      <div className="mt-6 flex flex-wrap gap-3">
        {entries.map(([cat, n]) => {
          const Icon = LucideIcons[categoryIcon(cat)] || LucideIcons.Sparkles;
          return (
            <Link key={cat} to={`/app/detected?category=${encodeURIComponent(cat)}`} className="flex items-center gap-3 border border-[var(--fcc-border)] rounded-2xl px-4 py-3 hover:bg-[var(--fcc-cream-2)] transition">
              <span className="w-9 h-9 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center"><Icon className="w-4 h-4 text-[var(--fcc-green)]" /></span>
              <div>
                <div className="font-display text-lg text-[var(--fcc-ink)] leading-none">{n}</div>
                <div className="text-xs text-[var(--fcc-muted)] mt-1">{cat}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function BriefingBanner({ preview }) {
  if (!preview) return null;
  const formatWeek = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return s; }
  };

  if (!preview.available) {
    return (
      <div
        data-testid={BRIEFING.dashboardBanner}
        className="fcc-card p-6 sm:p-7 flex flex-wrap items-center gap-5 border-2 border-dashed border-[var(--fcc-border)]"
      >
        <span className="w-12 h-12 rounded-2xl bg-[var(--fcc-green-soft)] flex items-center justify-center shrink-0">
          <BookOpen className="w-5 h-5 text-[var(--fcc-green)]" />
        </span>
        <div className="flex-1 min-w-[240px]">
          <p className="label-overline">Weekly Command Center</p>
          <h3 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">Generate this week&apos;s family briefing</h3>
          <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">
            One AI-written summary of what you need to know and do for {preview.week_start ? `the week of ${formatWeek(preview.week_start)}` : "the week"}.
          </p>
        </div>
        <Link
          to="/app/briefing"
          data-testid={BRIEFING.dashboardBannerOpen}
          className="fcc-btn-primary shrink-0"
        >
          Open Weekly Briefing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid={BRIEFING.dashboardBanner}
      className="fcc-card overflow-hidden grid lg:grid-cols-12"
    >
      <div className="lg:col-span-8 p-6 sm:p-8">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="fcc-pill fcc-chip-emma"><BookOpen className="w-3 h-3" /> This week&apos;s briefing</span>
          <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">
            {formatWeek(preview.week_start)} → {formatWeek(preview.week_end)}
          </span>
        </div>
        <p className="font-display text-2xl sm:text-3xl text-[var(--fcc-ink)] mt-4 leading-snug">
          {preview.family_headline}
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-[var(--fcc-muted)]">
          {preview.risk_count > 0 && (
            <span className="fcc-pill bg-[var(--fcc-red-soft)] text-[var(--fcc-red,#a23527)]">
              {preview.risk_count} risk{preview.risk_count > 1 ? "s" : ""}
            </span>
          )}
          {preview.conflict_count > 0 && (
            <span className="fcc-pill bg-[var(--fcc-peach)] text-[var(--fcc-ink)]">
              {preview.conflict_count} conflict{preview.conflict_count > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="mt-5">
          <Link
            to="/app/briefing"
            data-testid={BRIEFING.dashboardBannerOpen}
            className="fcc-btn-outline"
          >
            Read full briefing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <aside className="lg:col-span-4 p-6 sm:p-7 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
        <p className="label-overline">Top actions</p>
        <ul className="mt-4 space-y-2">
          {(preview.top_actions || []).slice(0, 3).map((a, i) => (
            <li key={i} className="bg-white border border-[var(--fcc-border)] rounded-xl p-3">
              <p className="text-sm font-semibold text-[var(--fcc-ink)] leading-snug">{a.title}</p>
              <p className="text-[10px] uppercase tracking-widest mt-1 text-[var(--fcc-muted)]">
                {a.child_name || "Family"} · {a.priority || "FYI"}
              </p>
            </li>
          ))}
          {(preview.top_actions || []).length === 0 && (
            <li className="text-sm text-[var(--fcc-muted)]">No urgent actions this week.</li>
          )}
        </ul>
      </aside>
    </div>
  );
}


const STATUS_STYLE = {
  "On Track":          { ring: "var(--fcc-green)",     bg: "var(--fcc-green-soft)", text: "var(--fcc-green)" },
  "Attention Needed":  { ring: "var(--fcc-orange)",    bg: "var(--fcc-peach)",      text: "var(--fcc-ink)"   },
  "Action Required":   { ring: "var(--fcc-red,#a23527)", bg: "var(--fcc-red-soft)", text: "var(--fcc-red,#a23527)" },
};

function HealthScoreCard({ health, history = [] }) {
  const style = STATUS_STYLE[health.status] || STATUS_STYLE["Attention Needed"];
  const dir = health.trend?.direction || "flat";
  const TrendIcon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const trendColor =
    dir === "up" ? "var(--fcc-green)"
    : dir === "down" ? "var(--fcc-red,#a23527)"
    : "var(--fcc-muted)";

  const breakdownRows = [
    { key: "missing_forms",       label: "Missing forms",           cta: "Review Forms",       to: "/app/tasks?filter=forms" },
    { key: "overdue_deadlines",   label: "Overdue deadlines",       cta: "Complete Task",      to: "/app/tasks?filter=overdue" },
    { key: "upcoming_deadlines",  label: "Deadlines within 3 days", cta: "Complete Task",      to: "/app/tasks?filter=upcoming" },
    { key: "unread_count",        label: "Unread child emails",     cta: "Review Messages",    to: "/app/detected" },
    { key: "unpaid_fees",         label: "Unpaid fees",              cta: "Resolve Payment",    to: "/app/tasks?filter=payments" },
    { key: "conflicts",           label: "Schedule conflicts",       cta: "Review Conflict",    to: "/app/briefing" },
  ];

  const sparkPts = [...history].reverse().slice(-8); // oldest → newest

  return (
    <div
      data-testid={HEALTH.card}
      className="fcc-card overflow-hidden grid lg:grid-cols-12"
    >
      <div className="lg:col-span-5 p-8 sm:p-10 flex flex-col items-start" style={{ background: style.bg }}>
        <p className="label-overline flex items-center gap-2"><Activity className="w-3 h-3" /> Family Health Score</p>
        <div className="mt-4 flex items-end gap-3">
          <span
            data-testid={HEALTH.score}
            className="font-display text-[88px] sm:text-[112px] leading-none text-[var(--fcc-ink)]"
            style={{ letterSpacing: "-0.03em" }}
          >
            {health.score}
          </span>
          <span className="text-2xl text-[var(--fcc-muted)] mb-3">/ 100</span>
        </div>
        <span
          data-testid={HEALTH.status}
          className="fcc-pill mt-4"
          style={{ background: "white", color: style.text, border: `1px solid ${style.ring}` }}
        >
          {health.status}
        </span>
        <div data-testid={HEALTH.trend} className="mt-4 flex items-center gap-2" style={{ color: trendColor }}>
          <TrendIcon className="w-4 h-4" />
          <span className="text-sm font-semibold">{health.trend?.label}</span>
        </div>
        {health.momentum && (
          <MomentumChip momentum={health.momentum} />
        )}
      </div>

      <div className="lg:col-span-7 p-8 sm:p-10">
        <p className="label-overline">What&apos;s pulling the score</p>
        <ul className="mt-4 space-y-2">
          {breakdownRows.map(r => {
            const v = health.breakdown[r.key] || 0;
            return (
              <li
                key={r.key}
                data-testid={HEALTH.breakdownRow}
                className="flex flex-wrap items-center justify-between gap-3 border border-[var(--fcc-border)] rounded-xl px-4 py-2.5"
              >
                <span className="text-sm text-[var(--fcc-ink-soft)]">{r.label}</span>
                <div className="flex items-center gap-3">
                  <span className={`fcc-pill ${v > 0 ? "bg-[var(--fcc-red-soft)] text-[var(--fcc-red,#a23527)]" : "bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]"}`}>{v}</span>
                  {v > 0 && (
                    <Link
                      to={r.to}
                      data-testid={`health-cta-${r.key}`}
                      className="text-xs font-semibold text-[var(--fcc-green)] hover:underline flex items-center gap-1"
                    >
                      {r.cta} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {sparkPts.length >= 2 && (
          <div className="mt-6">
            <p className="label-overline">Weekly trend</p>
            <div className="mt-3 flex items-end gap-1.5 h-16">
              {sparkPts.map((row) => {
                const h = Math.max(4, (row.score / 100) * 60);
                const color =
                  row.score >= 90 ? "var(--fcc-green)"
                  : row.score >= 70 ? "var(--fcc-orange)"
                  : "var(--fcc-red,#a23527)";
                return (
                  <div
                    key={row.week_start}
                    data-testid={HEALTH.historyRow}
                    title={`${row.week_start}: ${row.score}`}
                    className="flex-1 rounded-t-md"
                    style={{ height: `${h}px`, background: color }}
                  />
                );
              })}
            </div>
            <p className="text-[11px] uppercase tracking-widest mt-2 text-[var(--fcc-muted)]">
              {sparkPts.length} weeks · oldest → newest
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


function MomentumChip({ momentum }) {
  const tint = {
    "Improving": { bg: "#fff", color: "var(--fcc-green)",        border: "var(--fcc-green)" },
    "Stable":    { bg: "#fff", color: "var(--fcc-ink-soft)",     border: "var(--fcc-border)" },
    "Declining": { bg: "#fff", color: "var(--fcc-red,#a23527)",  border: "var(--fcc-red,#a23527)" },
  }[momentum.label] || { bg: "#fff", color: "var(--fcc-ink-soft)", border: "var(--fcc-border)" };
  const sign = momentum.score > 0 ? `+${momentum.score}` : momentum.score === 0 ? "0" : `${momentum.score}`;
  return (
    <div
      data-testid={HEALTH.momentum}
      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
      style={{ background: tint.bg, color: tint.color, border: `1px solid ${tint.border}` }}
      title={momentum.description}
    >
      <Sparkles className="w-3 h-3" />
      Momentum {sign} · {momentum.label}
    </div>
  );
}

